import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert, Button, Card, Descriptions, Input, Space, message, Table } from "antd";
import dayjs from "dayjs";
import { PageHeader } from "../../components/PageHeader";
import { DocLink } from "../../components/DocLink";
import { DocumentPrintSheet } from "../../components/DocumentPrintSheet";
import { QtyInput } from "../../components/QtyInput";
import { ReceiveDialog } from "../../components/ReceiveDialog";
import { SiteLabel } from "../../components/SiteLabel";
import { useApi } from "../../helpers/useApi";
import { hasAnyGrant } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import { DT_FORMATS } from "../../helpers/constants";
import { fmtSite } from "../../helpers/format";
import type { BoatNote, GIN, MRQ, Paginated } from "../../api-models";

interface DraftLine {
  mrqLineId: number;
  itemCode: string;
  itemName: string;
  unit?: string;
  outstanding: number;
  qty: number;
}

// A line is only dispatchable through this screen once it has a CW FulfillmentAllocation
// (spec §10) — outstanding is what's left of that allocation, not the raw requested qty.
function cwOutstanding(l: NonNullable<MRQ["lines"]>[number]): number {
  const cw = (l.allocations ?? []).filter((a) => a.sourceType === "CW" && a.status !== "CANCELLED");
  const allocated = cw.reduce((sum, a) => sum + (a.qty - a.cancelledQty), 0);
  const dispatched = cw.reduce((sum, a) => sum + a.dispatchedQty, 0);
  return allocated - dispatched;
}

// Why this screen has nothing to offer, in terms of what the request actually holds.
// Sending someone back to "go allocate" when their qty is already allocated — just to a
// non-CW source — is a dead end: procurement qty only becomes issuable after a GRN
// converts it to a CW allocation (spec §14.1/§31.19), and site-transfer qty never moves
// through an issue at all (spec §13).
function nothingToIssueReason(mrq: MRQ): { message: string; description: string; cta: string; to: string } {
  const outstanding: Record<string, number> = {};
  for (const l of mrq.lines ?? []) {
    for (const a of l.allocations ?? []) {
      if (a.status === "CANCELLED") continue;
      outstanding[a.sourceType] = (outstanding[a.sourceType] ?? 0) + (a.qty - a.cancelledQty - a.dispatchedQty);
    }
  }
  const procurement = (outstanding["PROCUREMENT_CW"] ?? 0) + (outstanding["PROCUREMENT_DIRECT"] ?? 0);
  const transfer = outstanding["SITE_TRANSFER"] ?? 0;

  if (procurement > 0.0005) {
    return {
      message: `${procurement} on this request is allocated to procurement, not to central stock`,
      description:
        "That quantity is a decision to buy, so there is nothing in the warehouse to dispatch yet. Raise a purchase requisition from the request; once its purchase order is received, the allocation becomes a central-warehouse one and this screen will have lines.",
      cta: "Raise requisition",
      to: `/requests/${mrq.id}`,
    };
  }
  if (transfer > 0.0005) {
    return {
      message: `${transfer} on this request is allocated from another site, not from central stock`,
      description: "Site-to-site quantity moves on a transfer order, not an issue.",
      cta: "Create transfer",
      to: `/transfers/new?mrqId=${mrq.id}`,
    };
  }
  return {
    message: "Nothing on this request is ready to dispatch from central stock",
    description:
      "An issue can only dispatch quantities that have a central-warehouse allocation. Lines are not added here — allocate them on the request first (Fulfillment allocation panel), then come back.",
    cta: "Open request",
    to: `/requests/${mrq.id}`,
  };
}

export default function IssueDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { request, loading } = useApi();
  const { user } = useUser();

  const isNew = id === "new";
  const mrqId = searchParams.get("mrqId");
  const [mrq, setMrq] = useState<MRQ | null>(null);
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [notes, setNotes] = useState("");
  const [gin, setGin] = useState<GIN | null>(null);
  const [mrqLoaded, setMrqLoaded] = useState(false);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [boatNotes, setBoatNotes] = useState<BoatNote[]>([]);
  const canReceive = hasAnyGrant(user, "MRN.Write");
  const canSeeBoatNotes = hasAnyGrant(user, "BoatNotes.Read");

  useEffect(() => {
    if (isNew && mrqId) {
      setMrqLoaded(false);
      request<MRQ>({ url: `/mrqs/${mrqId}` }).then((res) => {
        setMrq(res);
        setLines(
          (res.lines ?? [])
            .map((l) => ({
              mrqLineId: l.id,
              itemCode: l.item?.code ?? String(l.itemId),
              itemName: l.item?.name ?? "",
              unit: l.item?.unit?.abbreviation,
              outstanding: cwOutstanding(l),
              qty: cwOutstanding(l),
            }))
            .filter((l) => l.outstanding > 0.0005),
        );
        setMrqLoaded(true);
      });
    } else if (!isNew) {
      request<GIN>({ url: `/gins/${id}` }).then(setGin);
      // A cross-reference, not the page's subject: if the user cannot read boat notes the
      // section simply doesn't appear, rather than raising an error toast over the GIN.
      if (canSeeBoatNotes) {
        request<Paginated<BoatNote>>({ url: "/boat-notes", params: { ginId: id } }, { silent: true })
          .then((res) => setBoatNotes(res.data ?? []))
          .catch(() => setBoatNotes([]));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, mrqId]);

  async function submit() {
    if (!mrq) return;
    const items = lines.filter((l) => l.qty > 0).map((l) => ({ mrqLineId: l.mrqLineId, qty: l.qty }));
    if (items.length === 0) {
      message.error("Enter a quantity for at least one line.");
      return;
    }
    const created = await request<GIN>({ method: "POST", url: "/gins", data: { mrqId: mrq.id, notes, items } });
    message.success("Issue posted.");
    navigate(`/issues/${created.id}`, { replace: true });
  }

  if (isNew) {
    if (!mrqId) {
      return (
        <div>
          <PageHeader title="New Issue" />
          <Alert
            type="warning"
            showIcon
            message="No request selected"
            description="An issue is always posted against an approved material request. Open the request and use “Create issue” there."
            action={<Button onClick={() => navigate("/requests")}>Go to requests</Button>}
          />
        </div>
      );
    }
    return (
      <div>
        <PageHeader
          title="New Issue"
          subtitle={mrq ? <>{mrq.docNo} — <SiteLabel site={mrq.site} project={mrq.project} /></> : undefined}
          extra={
            <Button type="primary" onClick={submit} loading={loading} disabled={!mrq || lines.length === 0} data-tour="gin-post">
              Post issue
            </Button>
          }
        />
        {mrqLoaded && lines.length === 0 && mrq && (() => {
          const reason = nothingToIssueReason(mrq);
          return (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={reason.message}
              description={reason.description}
              action={<Button onClick={() => navigate(reason.to)}>{reason.cta}</Button>}
            />
          );
        })()}
        <Card size="small" title="Lines" data-tour="gin-lines">
          <Table
            rowKey="mrqLineId"
            dataSource={lines}
            pagination={false}
            locale={{ emptyText: "No central-warehouse allocations on this request." }}
            columns={[
              { title: "Code", dataIndex: "itemCode" },
              { title: "Item", dataIndex: "itemName" },
              { title: "Outstanding", render: (_, r) => `${r.outstanding} ${r.unit ?? ""}` },
              {
                title: "Issue qty",
                render: (_, r) => (
                  <QtyInput
                    value={r.qty}
                    unit={r.unit}
                    onChange={(v) =>
                      setLines((prev) => prev.map((l) => (l.mrqLineId === r.mrqLineId ? { ...l, qty: v ?? 0 } : l)))
                    }
                  />
                ),
              },
            ]}
          />
          <Input.TextArea style={{ marginTop: 12 }} placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
        </Card>
      </div>
    );
  }

  if (!gin) return null;

  const outstandingLines = (gin.lines ?? [])
    .map((l) => ({
      sourceLineId: l.id,
      itemCode: l.item?.code ?? String(l.itemId),
      itemName: l.item?.name ?? "",
      unit: l.item?.unit?.abbreviation,
      outstanding: l.dispatchedQty - l.receivedQty,
    }))
    .filter((l) => l.outstanding > 0.0005);

  return (
    <div>
      <PageHeader
        title={gin.docNo}
        subtitle={<SiteLabel site={gin.site} />}
        extra={
          <Space>
            {canReceive && outstandingLines.length > 0 && (
              <Button type="primary" onClick={() => setReceiveOpen(true)} data-tour="gin-receive">
                Receive at site
              </Button>
            )}
            <Button onClick={() => window.print()}>Print</Button>
          </Space>
        }
      />
      {outstandingLines.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="In transit"
          description="This dispatch is held in its own in-transit location until the site posts a receipt (MRN). The dispatched quantities below never change."
        />
      )}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Request">
            <DocLink docType="MRQ" id={gin.mrqId} docNo={gin.mrq?.docNo} />
          </Descriptions.Item>
          <Descriptions.Item label="Issued from">{gin.fromLocation?.name ?? "-"}</Descriptions.Item>
          <Descriptions.Item label="Dispatched at">{dayjs(gin.dispatchedAt).format(DT_FORMATS.dateTime)}</Descriptions.Item>
          {boatNotes.length > 0 && (
            <Descriptions.Item label={boatNotes.length > 1 ? "Boat notes" : "Boat note"}>
              <Space size={4} wrap>
                {boatNotes.map((b) => (
                  <DocLink key={b.id} docType="BOAT_NOTE" id={b.id} docNo={b.docNo} />
                ))}
              </Space>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>
      <Table
        rowKey="id"
        dataSource={gin.lines ?? []}
        pagination={false}
        columns={[
          { title: "Code", render: (_, r) => r.item?.code },
          { title: "Item", render: (_, r) => r.item?.name },
          { title: "Dispatched", dataIndex: "dispatchedQty" },
          { title: "Received", dataIndex: "receivedQty" },
          { title: "Outstanding", render: (_, r) => r.dispatchedQty - r.receivedQty },
        ]}
      />
      <ReceiveDialog
        open={receiveOpen}
        onClose={() => {
          setReceiveOpen(false);
          request<GIN>({ url: `/gins/${id}` }).then(setGin);
        }}
        sourceType="GIN"
        sourceId={gin.id}
        lines={outstandingLines}
      />
      <DocumentPrintSheet
        title="Issue / Delivery Note"
        docNo={gin.docNo}
        meta={[
          { label: "Site", value: fmtSite(gin.site) },
          { label: "Issued from", value: gin.fromLocation?.name ?? "" },
          { label: "Request", value: gin.mrq?.docNo ?? "" },
          { label: "Date", value: dayjs(gin.dispatchedAt).format(DT_FORMATS.date) },
        ]}
      >
        <Table
          rowKey="id"
          dataSource={gin.lines ?? []}
          pagination={false}
          columns={[
            { title: "Code", render: (_, r) => r.item?.code },
            { title: "Item", render: (_, r) => r.item?.name },
            { title: "Qty", dataIndex: "dispatchedQty" },
          ]}
        />
      </DocumentPrintSheet>
    </div>
  );
}
